import { useEffect, useRef, useState } from 'react';

import { AsyncThunk, AsyncThunkConfig } from '@reduxjs/toolkit';

import { List } from 'types/api';
import { Filters } from 'types/filters';

import { PAGE_SIZE } from 'constants/uiSettings';

import useErrorCatch from './useErrorCatch';
import { useAppDispatch } from './useTypedRedux';

interface UseInfiniteScrollPaginationProps<T, K> {
  // if reducer is responsible for data storing and updating
  loadItemsThunk?: AsyncThunk<T, Filters, AsyncThunkConfig>;
  errorMessage?: string;
  // list loader function, please cover with useCallback
  loadItems?: (filters: Filters) => Promise<T | undefined>;
}

const DEFAULT_ERROR_MESSAGE = 'Error loading items.';

const useInfiniteScrollPagination = <T extends List, K>({
  loadItemsThunk,
  errorMessage = DEFAULT_ERROR_MESSAGE,
  loadItems
}: UseInfiniteScrollPaginationProps<T, K>) => {
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<K[]>([]);

  const dispatch = useAppDispatch();
  const catchError = useErrorCatch();
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      setPage(0);
      setHasMore(true);
      setIsLoading(false);
      setData([]);
    };
  }, [loadItems]);

  const load = async (page: number = 0) => {
    const filters: Filters = {
      page,
      pageSize: PAGE_SIZE
    };

    try {
      setIsLoading(true);
      let res;

      if (loadItemsThunk) {
        res = (await dispatch(loadItemsThunk(filters)).unwrap()) as T;
      } else if (loadItems) {
        res = await loadItems(filters);
      }

      if (!res) {
        return;
      }

      setHasMore(!res.last);
      setPage(res.pageable.pageNumber);

      setData((prevData) => [...prevData, ...(res.content as K[])]);
    } catch (error) {
      catchError({
        error,
        default: {
          description: errorMessage
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const lastElementRef = (node: HTMLDivElement | null) => {
    if (isLoading) {
      return;
    }
    if (observer.current) {
      observer.current.disconnect();
    }
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        load(page + 1);
      }
    });
    if (node) {
      observer.current.observe(node);
    }
  };

  return { lastElementRef, data, isLoading };
};

export { useInfiniteScrollPagination };
